import React from 'react';
import _ from 'lodash';
import { Stack, Typography } from '@mui/material';
import { useGetProjectsPreviewQuery } from 'store/projects/projects';
import { ProjectPreview } from 'store/projects/interfaces';
import useGetSelectedEmployeePreview from 'hooks/useGetSelectedEmployeePreview';
import { useGetLoggedEmployeeQuery } from 'store/employee/employees';
import { LoggedEmployee } from 'store/employee/interfaces';
import { ProjectDataProps } from './interfaces';

function ProjectData({
  projectIri,
  projectPhaseIri,
  isExtended = false,
  employeeIri,
  variant = 'bold',
  color = 'text.primary',
  truncateLength = 27,
}: ProjectDataProps) {
  const { data: currentUser = {} as LoggedEmployee } =
    useGetLoggedEmployeeQuery();
  const { data: projectsForDisplayedEmployee = [] } =
    useGetProjectsPreviewQuery({});

  const { selectedEmployee } = useGetSelectedEmployeePreview({
    employeeIri: employeeIri ?? '',
    options: { skip: !employeeIri },
  });

  const projectData = projectsForDisplayedEmployee.find(
    (displayedEmployeeProject) =>
      displayedEmployeeProject['@id'] === projectIri,
  );

  const getProjectPhase = (
    project: ProjectPreview,
    phaseIri: string | null,
  ) => {
    if (phaseIri === null || !project?.projectPhases) {
      return null;
    }

    return project.projectPhases.find((phase) => phase['@id'] === phaseIri);
  };

  const getProjectDataText = (
    project: ProjectPreview | undefined,
    phaseIri: string | null,
  ) => {
    if (!project) {
      return '';
    }

    const projectPhase = getProjectPhase(project, phaseIri);
    if (!projectPhase) {
      return _.truncate(project.name, {
        length: truncateLength,
        omission: '...',
      });
    }

    if (isExtended) {
      return `${project.name} - ${projectPhase.name}`;
    }

    return _.truncate(`${project.name} - ${projectPhase.name}`, {
      length: truncateLength,
      omission: '...',
    });
  };

  const getEmployeeFullName = () => {
    if (!isExtended || !selectedEmployee) {
      return '';
    }

    const isSelectedEmployeeCurrentUser =
      currentUser.id === selectedEmployee.id;

    return isSelectedEmployeeCurrentUser
      ? ''
      : `(${selectedEmployee.lastname} ${selectedEmployee.firstname})`;
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.5}>
      <Typography variant={variant} color={color}>
        {getProjectDataText(projectData, projectPhaseIri)}
      </Typography>
      <Typography color="text.secondary">{getEmployeeFullName()}</Typography>
    </Stack>
  );
}

export default ProjectData;
